import {OperationVariables} from '@apollo/client';
import {useEffect, useMemo} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {pushToast} from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import {
  getSchemaOptionDataMetadata,
  shouldSkipOwnerQuery,
  useGetSchemaOwnerFromQuery,
} from 'schemas/SchemaOwnerUtil';
import type {TagSchemaSelectorOwnerQuery} from 'schemas/shared/__generated/TagSchemaSelectorOwnerQuery';
import {TAG_SCHEMA_SELECTOR_OWNER_INFO_QUERY} from 'schemas/shared/TagSchemaOrInterfaceSelector';
import {TAG_SCHEMA_SUMMARIES_QUERY} from 'schemas/useTagSchemaSummariesListingQuery';

import {resolveRoutineSchemaQuery, RoutineSchemaOption, RoutineSchemaSource} from './routineSchemaSources';

interface UseRoutineSchemaOptionsResult {
  schemas: Array<RoutineSchemaOption>;
  loading: boolean;
  error: boolean;
}

/**
 * Fetches the schemas available for a given routine data type using server-side filtering.
 *
 * The query fires as soon as a data type with a schema source is selected (rather than waiting for the
 * user to choose the "schema" scope), so the schema options are pre-loaded by the time the selector is
 * shown. The query used depends on the {@link RoutineSchemaSource} (tag schemas, study schemas, etc.).
 *
 * All schemas are returned, including archived ones, so that editing a routine whose schema was later
 * archived still shows the existing selection. On failure the user is shown an error toast.
 *
 * For multi-org tenants, schemas with duplicate names are disambiguated by appending the owning
 * organization's name.
 */
export default function useRoutineSchemaOptions(
  schemaSource: RoutineSchemaSource | null
): UseRoutineSchemaOptionsResult {
  const resolved = schemaSource != null ? resolveRoutineSchemaQuery(schemaSource) : null;

  const result = useSkippableBenchlingQuery<unknown, OperationVariables>(
    // Apollo requires a query argument even when skipped; this is never executed.
    resolved?.query ?? TAG_SCHEMA_SUMMARIES_QUERY,
    {variables: resolved?.variables ?? {}, skip: resolved == null}
  );

  const ownerResult = useSkippableBenchlingQuery<TagSchemaSelectorOwnerQuery>(
    TAG_SCHEMA_SELECTOR_OWNER_INFO_QUERY,
    {skip: shouldSkipOwnerQuery() || resolved == null}
  );

  const getSchemaOwner = useGetSchemaOwnerFromQuery<TagSchemaSelectorOwnerQuery>({
    result: ownerResult,
    getIdOwnerTuples: (data) =>
      data.tagSchemas.map((schema) => ({
        id: schema.id,
        owner: schema.registry.organization.name,
      })),
  });

  const isError = result.type === QueryResultType.ERROR;
  useEffect(() => {
    if (isError) {
      pushToast({message: 'Unable to load schemas. Please try again.', type: ToastType.DANGER});
    }
  }, [isError]);

  const rawSchemas = useMemo(() => {
    if (result.type !== QueryResultType.SUCCESS || resolved == null) {
      return [];
    }
    return resolved.extractSchemas(result.data);
  }, [result, resolved]);

  const schemas = useMemo(() => {
    if (rawSchemas.length === 0) {
      return rawSchemas;
    }
    const getOwner = (schema: RoutineSchemaOption): string | undefined =>
      getSchemaOwner(schema) ?? schema.owner;
    const getMetadata = getSchemaOptionDataMetadata(rawSchemas, getOwner);
    return rawSchemas.map((schema) => {
      const owner = getMetadata(schema);
      if (owner == null) {
        return schema;
      }
      return {...schema, name: `${schema.name} (${owner})`};
    });
  }, [rawSchemas, getSchemaOwner]);

  if (result.type === QueryResultType.SKIPPED || result.type === QueryResultType.PENDING) {
    return {schemas: [], loading: result.type === QueryResultType.PENDING, error: false};
  }

  if (result.type === QueryResultType.ERROR) {
    return {schemas: [], loading: false, error: true};
  }

  return {schemas, loading: false, error: false};
}
